// Referral partner CRM.
//
// Manage the referral network — doctor offices, Medicare counselors,
// HR departments, real estate agents, financial planners, community
// orgs. Every partner has category + priority + status + touch
// history. Cold-partner alerts surface partners with no referral in
// 60d so you can intervene before the loop dies.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import { useIsDemoMode, useDemoReferralPartners } from '../demo/DemoDataProvider';

async function api(method, body = null, query = '') {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(`/api/subscriber/referral-partners${query}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body: j };
}

const CATEGORY_LABELS = {
  doctor_office:      { label: 'Doctor Office',     tone: 'bg-rose-100 text-rose-900' },
  medicare_counselor: { label: 'Medicare Counselor', tone: 'bg-blue-100 text-blue-900' },
  hr_department:      { label: 'HR Department',     tone: 'bg-emerald-100 text-emerald-900' },
  real_estate_agent:  { label: 'Real Estate',       tone: 'bg-amber-100 text-amber-900' },
  financial_advisor:  { label: 'Financial Advisor', tone: 'bg-purple-100 text-purple-900' },
  insurance_agent:    { label: 'Insurance Agent',   tone: 'bg-teal-100 text-teal-900' },
  community_org:      { label: 'Community Org',     tone: 'bg-fuchsia-100 text-fuchsia-900' },
  attorney:           { label: 'Attorney',          tone: 'bg-slate-200 text-slate-800' },
  cpa:                { label: 'CPA',               tone: 'bg-orange-100 text-orange-900' },
  other:              { label: 'Other',             tone: 'bg-slate-100 text-slate-700' },
};

function fmtDate(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  const daysAgo = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (daysAgo === 0)   return 'today';
  if (daysAgo === 1)   return 'yesterday';
  if (daysAgo < 30)    return `${daysAgo}d ago`;
  if (daysAgo < 365)   return `${Math.floor(daysAgo / 30)}mo ago`;
  return d.toLocaleDateString();
}

function conversionRate(p) {
  if (!p.total_referrals) return null;
  return Math.round((p.converted_referrals || 0) / p.total_referrals * 100);
}

function PartnerForm({ initial, onCancel, onSaved, showToast }) {
  const isEdit = !!initial?.id;
  const [f, setF] = React.useState({
    name:     initial?.name     || '',
    org:      initial?.org      || '',
    category: initial?.category || 'other',
    email:    initial?.email    || '',
    phone:    initial?.phone    || '',
    address:  initial?.address  || '',
    priority: String(initial?.priority ?? 100),
    status:   initial?.status   || 'active',
    notes:    initial?.notes    || '',
  });
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    const payload = { ...f, priority: Number(f.priority) };
    const { ok, body } = isEdit
      ? await api('PATCH', { id: initial.id, patch: payload })
      : await api('POST',  payload);
    setSaving(false);
    if (!ok) return showToast(`${isEdit ? 'Update' : 'Add'} failed: ${body?.error || 'unknown'}${body?.detail ? ` (${body.detail})` : ''}`);
    onSaved?.(body.partner);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{isEdit ? 'Edit partner' : 'Add referral partner'}</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Name</span>
          <input value={f.name} onChange={e => set('name', e.target.value)}
            placeholder="Dr. Sarah Miller"
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Organization</span>
          <input value={f.org} onChange={e => set('org', e.target.value)}
            placeholder="St. Mary's Family Practice"
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Category</span>
          <select value={f.category} onChange={e => set('category', e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm">
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Email</span>
          <input type="email" value={f.email} onChange={e => set('email', e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Phone</span>
          <input value={f.phone} onChange={e => set('phone', e.target.value)}
            placeholder="+15551234567"
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="col-span-2 text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Address</span>
          <input value={f.address} onChange={e => set('address', e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Priority (0–1000)</span>
          <input type="number" min="0" max="1000" value={f.priority}
            onChange={e => set('priority', e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Status</span>
          <select value={f.status} onChange={e => set('status', e.target.value)}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm">
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="removed">removed</option>
          </select>
        </label>
        <label className="col-span-2 text-xs">
          <span className="block mb-1 font-semibold text-slate-700">Notes</span>
          <textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2}
            className="w-full rounded border-slate-300 px-2 py-1.5 text-sm" />
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
          className="rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving || !f.name.trim()}
          className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add partner')}
        </button>
      </div>
    </div>
  );
}

function PartnerCard({ partner, isCold, onEdit, onTouch, onRemove }) {
  const cat = CATEGORY_LABELS[partner.category] || CATEGORY_LABELS.other;
  const convRate = conversionRate(partner);
  const dead     = partner.status === 'removed';

  return (
    <div className={`rounded-lg border p-3 shadow-sm ${
      isCold ? 'border-amber-300 bg-amber-50' : dead ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`rounded px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider ${cat.tone}`}>{cat.label}</span>
            {isCold && (
              <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-amber-900">Cold</span>
            )}
            {partner.status === 'paused' && (
              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-slate-700">Paused</span>
            )}
            <span className="text-[0.6rem] font-semibold text-slate-500">P{partner.priority}</span>
          </div>
          <div className="mt-1 font-semibold text-slate-800 truncate">{partner.name}</div>
          {partner.org && <div className="text-xs text-slate-600 truncate">{partner.org}</div>}
          <div className="text-[0.65rem] text-slate-500 truncate">
            {partner.email || '—'}{partner.phone ? ` · ${partner.phone}` : ''}
          </div>
          <div className="mt-1.5 grid grid-cols-3 gap-2 text-[0.65rem]">
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[0.55rem]">Referrals</div>
              <div className="font-bold text-slate-800">{partner.total_referrals || 0}</div>
            </div>
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[0.55rem]">Converted</div>
              <div className="font-bold text-slate-800">
                {partner.converted_referrals || 0}
                {convRate != null && <span className="text-[0.55rem] text-slate-500 ml-1">({convRate}%)</span>}
              </div>
            </div>
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[0.55rem]">Last referral</div>
              <div className="font-bold text-slate-800">{fmtDate(partner.last_referral_at)}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {partner.phone && (
            <a href={`tel:${partner.phone}`}
              className="rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-900 hover:bg-blue-200"
            >📞 Call</a>
          )}
          {!dead && (
            <>
              <button type="button" onClick={() => onEdit(partner)}
                className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                Edit
              </button>
              <button type="button" onClick={() => onTouch(partner)}
                className="rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-900 hover:bg-emerald-200">
                Log touch
              </button>
              <button type="button" onClick={() => onRemove(partner)}
                className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50">
                Remove
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReferralPartnersView({ currentUser, showToast = () => {} }) {
  const [partners, setPartners] = React.useState([]);
  const [summary, setSummary]   = React.useState(null);
  const [loading, setLoading]   = React.useState(true);
  const [adding, setAdding]     = React.useState(false);
  const [editing, setEditing]   = React.useState(null);
  const [categoryFilter, setCategoryFilter] = React.useState('all');

  const isDemo         = useIsDemoMode();
  const demoPartners   = useDemoReferralPartners();

  const refresh = React.useCallback(async () => {
    setLoading(true);
    if (isDemo && Array.isArray(demoPartners)) {
      const cutoffMs = Date.now() - 60 * 86400_000;
      const cutoffIso = new Date(cutoffMs).toISOString();
      const active = demoPartners.filter(p => p.status === 'active');
      const cold   = active.filter(p => !p.last_referral_at || p.last_referral_at < cutoffIso);
      setPartners(demoPartners);
      setSummary({
        total:               demoPartners.length,
        active:              active.length,
        paused:              demoPartners.filter(p => p.status === 'paused').length,
        removed:             demoPartners.filter(p => p.status === 'removed').length,
        cold:                cold.length,
        cold_threshold_days: 60,
        total_referrals:     active.reduce((a, p) => a + (p.total_referrals || 0), 0),
        total_converted:     active.reduce((a, p) => a + (p.converted_referrals || 0), 0),
      });
      setLoading(false);
      return;
    }
    const { ok, body } = await api('GET');
    if (ok) {
      setPartners(body.partners || []);
      setSummary(body.summary || null);
    } else {
      showToast(`Load failed: ${body?.error || 'unknown'}`);
    }
    setLoading(false);
  }, [showToast, isDemo, demoPartners]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const logTouch = async (p) => {
    const { ok, body } = await api('POST', { id: p.id }, '?action=touch');
    if (!ok) return showToast(`Touch failed: ${body?.error || 'unknown'}`);
    showToast(`Logged touch with ${p.name}`);
    refresh();
  };

  const removePartner = async (p) => {
    const confirmed = await (window.hqConfirm ? window.hqConfirm({
      title: `Remove ${p.name}?`,
      body: 'They\'ll stop appearing in the active list.',
      confirmLabel: 'Remove partner', danger: true,
    }) : Promise.resolve(window.confirm(`Remove ${p.name}?`)));
    if (!confirmed) return;
    const { ok, body } = await api('DELETE', { id: p.id });
    if (!ok) return showToast(`Failed: ${body?.error || 'unknown'}`);
    showToast('Partner removed');
    refresh();
  };

  const cutoff = summary?.cold_threshold_days
    ? new Date(Date.now() - summary.cold_threshold_days * 86400_000).toISOString()
    : null;
  const isCold = (p) => p.status === 'active' && (!p.last_referral_at || (cutoff && p.last_referral_at < cutoff));

  const filtered = partners.filter(p => {
    if (categoryFilter === 'all')       return true;
    if (categoryFilter === 'cold')      return isCold(p);
    return p.category === categoryFilter;
  });

  const active   = filtered.filter(p => p.status === 'active');
  const paused   = filtered.filter(p => p.status === 'paused');
  const removed  = filtered.filter(p => p.status === 'removed');

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">Referral Partners</h1>
          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-fuchsia-900">
            Growth loops
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Track the doctor offices, Medicare counselors, HR departments, and community orgs
          that send you business. Cold-partner alerts flag anyone with no referral in{' '}
          {summary?.cold_threshold_days || 60}d so you can intervene before the loop dies.
        </p>
      </header>

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Active partners</div>
            <div className="text-lg font-bold">{summary.active}</div>
          </div>
          <div className={`rounded-lg border p-3 ${summary.cold > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
            <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Cold ({summary.cold_threshold_days}d)</div>
            <div className={`text-lg font-bold ${summary.cold > 0 ? 'text-amber-800' : ''}`}>{summary.cold}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Total referrals</div>
            <div className="text-lg font-bold">{summary.total_referrals}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Converted</div>
            <div className="text-lg font-bold">
              {summary.total_converted}
              <span className="text-xs text-slate-500 ml-1">
                ({summary.total_referrals > 0 ? Math.round(summary.total_converted / summary.total_referrals * 100) : 0}%)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Filter row + add */}
      <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setCategoryFilter('all')}
            className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${categoryFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >All ({partners.length})</button>
          {summary?.cold > 0 && (
            <button
              type="button"
              onClick={() => setCategoryFilter('cold')}
              className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${categoryFilter === 'cold' ? 'ring-2 ring-amber-500 ' : ''}bg-amber-200 text-amber-900 hover:bg-amber-300`}
            >Cold ({summary.cold})</button>
          )}
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => {
            const n = partners.filter(p => p.category === k).length;
            if (n === 0) return null;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setCategoryFilter(k)}
                className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${categoryFilter === k ? 'ring-2 ring-blue-400 ' : ''}${v.tone} hover:brightness-95`}
              >{v.label} ({n})</button>
            );
          })}
        </div>
        <button type="button" onClick={() => { setAdding(true); setEditing(null); }}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
          disabled={adding}>
          + Add partner
        </button>
      </div>

      {adding && (
        <div className="mb-4">
          <PartnerForm
            initial={null}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); refresh(); }}
            showToast={showToast}
          />
        </div>
      )}
      {editing && (
        <div className="mb-4">
          <PartnerForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
            showToast={showToast}
          />
        </div>
      )}

      {!loading && filtered.length === 0 && !adding && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          {partners.length === 0
            ? 'No partners yet. Add your best-performing referral sources so cold-partner alerts start working.'
            : 'No partners match this filter.'}
        </div>
      )}

      <div className="space-y-3">
        {active.map(p => (
          <PartnerCard key={p.id} partner={p} isCold={isCold(p)}
            onEdit={setEditing} onTouch={logTouch} onRemove={removePartner} />
        ))}
        {paused.length > 0 && (
          <div className="pt-3">
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Paused</h2>
            {paused.map(p => (
              <div key={p.id} className="mb-2">
                <PartnerCard partner={p} isCold={false}
                  onEdit={setEditing} onTouch={logTouch} onRemove={removePartner} />
              </div>
            ))}
          </div>
        )}
        {removed.length > 0 && (
          <details className="pt-3">
            <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-500">
              Removed ({removed.length})
            </summary>
            <div className="mt-2 space-y-2">
              {removed.map(p => (
                <PartnerCard key={p.id} partner={p} isCold={false}
                  onEdit={setEditing} onTouch={logTouch} onRemove={removePartner} />
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
